import React, {useCallback, useEffect, useState} from 'react';
import {classNames} from 'primereact/utils';
import {
    getNetworkQualityTier,
    NETWORK_CHAIN_POLL_INTERVAL_MS,
    probeNetworkReachabilityGetChain,
    useNetworkConnectionMetrics,
} from '../../utils/internetReachability';
import './LoginEnvironmentCheck.scss';

export type EnvCheckKey = 'sound' | 'microphone' | 'camera' | 'network';

type RowState = 'loading' | 'ok' | 'error';

const ROWS: {key: EnvCheckKey; label: string}[] = [
    {key: 'sound', label: 'Звук'},
    {key: 'microphone', label: 'Мікрофон'},
    {key: 'camera', label: 'Камера'},
    {key: 'network', label: 'Доступність мережі'},
];

const OK_ICONS: Record<EnvCheckKey, string> = {
    sound: 'pi-volume-up',
    microphone: 'pi-microphone',
    camera: 'pi-camera',
    network: 'pi-wifi',
};

const HEADER_ERROR_ICON_CLASSES = 'pi-info-circle pi-fw';

async function checkSoundCapability(): Promise<boolean> {
    try {
        const AC = window.AudioContext || (window as unknown as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
        if (!AC) {
            return false;
        }
        const ctx = new AC();
        await ctx.close();
        return true;
    } catch {
        return false;
    }
}

async function checkMicrophoneAndCamera(): Promise<{microphone: RowState; camera: RowState}> {
    if (!navigator.mediaDevices?.getUserMedia) {
        return {microphone: 'error', camera: 'error'};
    }

    const tryCombo = async (): Promise<{microphone: RowState; camera: RowState} | null> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true, video: true});
            stream.getTracks().forEach((t) => t.stop());
            return {microphone: 'ok', camera: 'ok'};
        } catch {
            return null;
        }
    };

    const combo = await tryCombo();
    if (combo) {
        return combo;
    }

    let microphone: RowState = 'error';
    let camera: RowState = 'error';

    try {
        const audioStream = await navigator.mediaDevices.getUserMedia({audio: true});
        audioStream.getTracks().forEach((t) => t.stop());
        microphone = 'ok';
    } catch {
        microphone = 'error';
    }

    try {
        const videoStream = await navigator.mediaDevices.getUserMedia({video: true});
        videoStream.getTracks().forEach((t) => t.stop());
        camera = 'ok';
    } catch {
        camera = 'error';
    }

    return {microphone, camera};
}

export const LoginEnvironmentCheck = () => {
    const networkMetrics = useNetworkConnectionMetrics();

    const [checks, setChecks] = useState<Record<EnvCheckKey, RowState>>(() =>
        ROWS.reduce(
            (acc, r) => {
                acc[r.key] = 'loading';
                return acc;
            },
            {} as Record<EnvCheckKey, RowState>,
        ),
    );

    const runChecks = useCallback(() => {
        setChecks(
            ROWS.reduce(
                (acc, r) => {
                    acc[r.key] = 'loading';
                    return acc;
                },
                {} as Record<EnvCheckKey, RowState>,
            ),
        );

        let cancelled = false;
        const patch = (partial: Partial<Record<EnvCheckKey, RowState>>) => {
            if (!cancelled) {
                setChecks((prev) => ({...prev, ...partial}));
            }
        };

        checkSoundCapability().then((ok) => patch({sound: ok ? 'ok' : 'error'}));

        checkMicrophoneAndCamera().then(({microphone, camera}) => patch({microphone, camera}));

        probeNetworkReachabilityGetChain().then((ok) => patch({network: ok ? 'ok' : 'error'}));

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const cleanup = runChecks();
        return cleanup;
    }, [runChecks]);

    useEffect(() => {
        const onOnline = () => {
            void probeNetworkReachabilityGetChain().then((ok) => {
                setChecks((prev) => ({...prev, network: ok ? 'ok' : 'error'}));
            });
        };
        const onOffline = () => {
            setChecks((prev) => ({...prev, network: 'error'}));
        };
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const intervalId = window.setInterval(() => {
            void probeNetworkReachabilityGetChain().then((ok) => {
                if (!cancelled) {
                    setChecks((prev) => ({...prev, network: ok ? 'ok' : 'error'}));
                }
            });
        }, NETWORK_CHAIN_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, []);

    return (
        <div className="login-env-check" aria-live="polite">
            <ul className="login-env-check__list">
                {ROWS.map(({key, label}) => {
                    const state = checks[key];
                    const networkOkTier =
                        key === 'network' && state === 'ok'
                            ? getNetworkQualityTier(true, networkMetrics.downlink, networkMetrics.effectiveType)
                            : null;
                    return (
                        <li key={key} className="login-env-check__row">
                            <span className="login-env-check__icon" aria-hidden>
                                {state === 'loading' && <i className="pi pi-spin pi-spinner login-env-check__spinner" />}
                                {state === 'ok' && (
                                    <i
                                        className={classNames(
                                            'pi',
                                            OK_ICONS[key],
                                            'login-env-check__ok',
                                            networkOkTier != null && networkOkTier >= 1 && `login-env-check__ok--network-tier-${networkOkTier}`,
                                        )}
                                    />
                                )}
                                {state === 'error' &&
                                    (key === 'network' ? (
                                        <i className={classNames('pi', OK_ICONS.network, 'login-env-check__network-err-wifi')} />
                                    ) : (
                                        <i className={classNames('pi', HEADER_ERROR_ICON_CLASSES, 'login-env-check__header-err')} />
                                    ))}
                            </span>
                            <span className="login-env-check__label">{label}</span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
